<template>
  <div class="hb-games">
    <div v-if="!embedded" class="hb-table-title">{{ $t('matchList') }} <span v-if="total" class="hb-table-count">({{ total }})</span></div>

    <!-- LOADING -->
    <div v-if="loading" class="hb-table-empty">
      {{ $t('loading') }}
    </div>

    <!-- EMPTY -->
    <div v-else-if="!rows.length" class="hb-table-empty">
      {{ $t('noStatsYet') }}
    </div>

    <!-- TABLE -->
    <div v-else>
      <div class="hb-table-wrap"
        :class="{ 'fade-left': canScrollLeft, 'fade-right': canScrollRight, 'fade-past-col': showStickyShadow }">
        <div ref="tableScrollRef" class="hb-table-scroll"
          @scroll="updateScrollFade">
        <table class="hb-table">
          <thead>
            <tr>
              <th class="th-date hb-sortable" @click="sortBy('date')" :class="{ active: isActive('date') }">{{ $t('date') }}</th>
              <th class="th-opponent hb-col-sticky hb-col-sticky-shadow hb-sortable" style="left: 0" @click="sortBy('opponent')" :class="{ active: isActive('opponent') }">{{ $t('opponent') }}</th>
              <template v-if="isSkater">
                <th class="hb-stat-header hb-sortable" @click="sortBy('g')" :class="{ active: isActive('g') }">{{ $t('G') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('a')" :class="{ active: isActive('a') }">{{ $t('A') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('p')" :class="{ active: isActive('p') }">{{ $t('P') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('pim')" :class="{ active: isActive('pim') }">{{ $t('PIM') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('ppg')" :class="{ active: isActive('ppg') }">{{ $t('PPG') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('shg')" :class="{ active: isActive('shg') }">{{ $t('SHG') }}</th>
              </template>
              <template v-else>
                <th class="hb-stat-header hb-sortable" @click="sortBy('sa')" :class="{ active: isActive('sa') }">{{ $t('SA') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('sv')" :class="{ active: isActive('sv') }">{{ $t('SV') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('ga')" :class="{ active: isActive('ga') }">{{ $t('GA') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('svpct')" :class="{ active: isActive('svpct') }">{{ $t('SV%') }}</th>
                <th class="hb-stat-header hb-sortable" @click="sortBy('toi')" :class="{ active: isActive('toi') }">{{ $t('TOI') }}</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in sortedRows" :key="r._id"
              @click="router.push(`/gamecenter/${r.event._id}`)">
              <td class="td-date" :class="{ 'col-active': isActive('date') }">{{ formatDate(r.date) }}</td>
              <td class="td-opponent hb-col-sticky hb-col-sticky-shadow" style="left: 0" :class="{ 'col-active': isActive('opponent') }">
                <div class="hb-opp-team">
                  <img v-if="r.opponent?.imageUrl" :src="teamLogoUrl(r.opponent?.imageUrl)" alt="" class="hb-opp-logo" />
                  <div v-else class="hb-opp-placeholder">{{ getInitial(r.opponent?.name) }}</div>
                  <span>{{ getShotcut(r.opponent?.name) || '-' }}</span>
                </div>
              </td>
              <template v-if="isSkater">
                <td class="hb-stat" :class="[statClass((r as any).g, true), { 'col-active': isActive('g') }]">{{ (r as any).g }}</td>
                <td class="hb-stat" :class="[statClass((r as any).a), { 'col-active': isActive('a') }]">{{ (r as any).a }}</td>
                <td class="hb-stat" :class="[statClass((r as any).p, true), { 'col-active': isActive('p') }]">{{ (r as any).p }}</td>
                <td class="hb-stat" :class="[statClass((r as any).pim), { 'col-active': isActive('pim') }]">{{ (r as any).pim }}</td>
                <td class="hb-stat" :class="[statClass((r as any).ppg), { 'col-active': isActive('ppg') }]">{{ (r as any).ppg }}</td>
                <td class="hb-stat" :class="[statClass((r as any).shg), { 'col-active': isActive('shg') }]">{{ (r as any).shg }}</td>
              </template>
              <template v-else>
                <td class="hb-stat" :class="[statClass((r as any).sa), { 'col-active': isActive('sa') }]">{{ (r as any).sa }}</td>
                <td class="hb-stat" :class="[statClass((r as any).sv), { 'col-active': isActive('sv') }]">{{ (r as any).sv }}</td>
                <td class="hb-stat" :class="[statClass((r as any).ga), { 'col-active': isActive('ga') }]">{{ (r as any).ga }}</td>
                <td class="hb-stat" :class="[statClass((r as any).svpct), { 'col-active': isActive('svpct') }]">{{ formatSvCell((r as any).svpct) }}</td>
                <td class="hb-stat" :class="[statClass((r as any).toi), { 'col-active': isActive('toi') }]">{{ formatTOI((r as any).toi) }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      </div>

      <div v-if="!embedded && rows.length < total" style="padding: 0 4px; margin-top: 8px;">
        <button class="load-more-btn" :disabled="loadingMore" @click="loadMore">
          <v-progress-circular v-if="loadingMore" indeterminate size="16" width="2" />
          <template v-else>{{ $t('loadMore') }} <span class="load-more-count">({{ total - rows.length }})</span></template>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import axiosInstance from '@/services/axiosInstance'
import { useI18n } from 'vue-i18n'
import { getTeamImage } from '@/utils/imageUtils'
import { useRouter } from 'vue-router'
import { getShotcut } from '@/utils/teamUtils';
import { useDisplay } from 'vuetify';

const router = useRouter()

const { t } = useI18n({ useScope: 'global' })
const { smAndDown } = useDisplay()

/** Get first letter of team name for placeholder logo */
const getInitial = (name?: string | null) => (name ? name.charAt(0).toUpperCase() : '?')

/** CSS class for stat value — ice highlight for key stats, zero dimming */
const statClass = (val: number | null | undefined, highlight = false) => {
  if (val === null || val === undefined) return 'zero'
  if (val === 0) return 'zero'
  if (highlight && val > 0) return 'ice'
  return ''
}

// 🧩 Props
const props = defineProps<{
  userId: string
  role: 'g' | 'p'     // g = goalie, p = player (skater)
  leagueId?: string
  /** When true, hide internal title & load-more (for external ios-section layout) */
  embedded?: boolean
}>()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

// ---- UI state
const loading = ref(false)
const rawResponse = ref<any>(null)

// ---- interní datové typy
type OpponentInfo = { name?: string | null; imageUrl?: string | null }

type RowSkater = {
  _id: string
  date: string | null
  opponent: OpponentInfo
  g: number
  a: number
  p: number
  pim: number
  ppg: number
  shg: number
}
type RowGoalie = {
  _id: string
  date: string | null
  opponent: OpponentInfo
  sa: number
  sv: number
  ga: number
  svpct: number | null
  toi: number
}





const rows = ref<Array<RowSkater | RowGoalie>>([])
const tableScrollRef = ref<HTMLElement | null>(null)

// Scroll fade indicators
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const showStickyShadow = ref(false)

const updateScrollFade = () => {
  const el = tableScrollRef.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 2
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
  // Show sticky shadow only when date column is fully scrolled behind opponent
  const dateCol = el.querySelector('.th-date, .td-date') as HTMLElement | null
  const dateWidth = dateCol ? dateCol.offsetWidth : 90
  showStickyShadow.value = el.scrollLeft >= dateWidth
}

// ---- pomocné funkce
const isSkater = computed(() => props.role === 'p')
const roleParam = computed(() => (props.role === 'g' ? 'goalie' : 'skater'))

const num = (n: any, d = 0) => (typeof n === 'number' && !Number.isNaN(n) ? n : d)
const teamLogoUrl = (raw?: string | null) => getTeamImage(raw || '')
const formatDate = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'

  if (smAndDown.value) {
    return d.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'numeric',
      year: '2-digit',
    })
  }

  // české locale, např. "28. října 2025"
  const formatted = d.toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'long',
    year: '2-digit', // pro "25" místo "2025"
  })

  // volitelné: první písmeno měsíce malé (toLocaleDateString dává velké)
  return formatted.replace(/([A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ])/u, (c) => c.toLowerCase())
}

const formatTOI = (secs?: number | null) => {
  const s = num(secs, 0)
  const m = Math.floor(s / 60)
  const rest = Math.floor(s % 60)
  return `${m}:${rest.toString().padStart(2, '0')}`
}
const formatSvCell = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—'
  const v = Number(value)
  if (Number.isNaN(v)) return '—'
  const frac = v > 1.5 ? v / 100 : v
  const clamped = Math.min(Math.max(frac, 0), 0.999)
  return (clamped.toFixed(3)).slice(1)
}



// ---- FETCH
const limit = ref(5)
const offset = ref(0)
const total = ref(0)
const loadingMore = ref(false)

const fetchRecent = async (append = false) => {
  if (!props.userId) return
  
  if (append) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  try {
    const { data } = await axiosInstance.get(
      `${API_BASE_URL}/stats/user/${props.userId}/recent-games`,
      { 
        params: { 
          role: roleParam.value, 
          leagueId: props.leagueId,
          limit: limit.value,
          offset: offset.value,
          sortBy: sort.value.key || 'date',
          sortOrder: sort.value.order === 1 ? 'asc' : 'desc'
        } 
      }
    )
    //console.log('Fetched recent games data:', props.userId)
    const games = Array.isArray(data?.games) ? data.games : []
    total.value = data?.total || 0
    rawResponse.value = data
    //console.log('✅ Recent games data:', data)
    
    const mappedRows = games.map((g: any) => {
      const stats = g?.stats || {}
      const common = stats.common || {}
      const skater = stats.skater || {}
      const goalie = stats.goalie || {}
      const opponent = g.opponentTeam || {}

      if (isSkater.value) {
        return {
          event: g.event || null,
          _id: g._id,
          date: g.date ?? null,
          opponent: { name: opponent.name ?? null, imageUrl: opponent.imageUrl ?? null },
          g: num(skater.goals),
          a: num(skater.assists),
          p: num(skater.points),
          pim: Math.round(num(common.penaltySeconds) / 60),
          ppg: num(skater.powerPlayGoals),
          shg: num(skater.shortHandedGoals),
        } as RowSkater
      } else {
        return {
          event: g.event || null,
          _id: g._id,
          date: g.date ?? null,
          opponent: { name: opponent.name ?? null, imageUrl: opponent.imageUrl ?? null },
          sa: num(goalie.shotsAgainst),
          sv: num(goalie.saves),
          ga: num(goalie.goalsAgainst),
          svpct: typeof goalie.savePct === 'number' ? goalie.savePct : null,
          toi: num(common.timeOnIceTotal),
        } as RowGoalie
      }
    })

    if (append) {
      rows.value = [...rows.value, ...mappedRows]
    } else {
      rows.value = mappedRows
    }

  } catch (e) {
    console.error('❌ Failed to load recent games:', e)
    if (!append) rows.value = []
  } finally {
    loading.value = false
    loadingMore.value = false
    nextTick(updateScrollFade)
  }
}

const loadMore = () => {
  offset.value += limit.value
  limit.value = 10
  fetchRecent(true)
}

// ---- SORTING
// ---- SORTING
type SortKeySk = 'date' | 'opponent' | 'g' | 'a' | 'p' | 'pim' | 'sog' | 'ppg' | 'shg'
type SortKeyGk = 'date' | 'opponent' | 'sa' | 'sv' | 'ga' | 'svpct' | 'toi'

// ⚙️ výchozí řazení podle data, ale bez aktivního highlightu
const sort = ref<{ key: SortKeySk | SortKeyGk | null; order: 1 | -1 }>({
  key: null,
  order: -1,
})

// ⏰ computed, které určí, podle čeho řadit (defaultně date)
const effectiveSortKey = computed(() => sort.value.key ?? 'date')

// 🧭 klikání na hlavičky
const sortBy = async (key: SortKeySk | SortKeyGk) => {
  // Uložit aktuální scroll pozici
  const scrollLeft = tableScrollRef.value?.scrollLeft ?? 0
  
  // první klik na sloupec = zapne highlight
  if (sort.value.key !== key) {
    sort.value = { key, order: -1 }
  } else {
    // další klik = otočí pořadí
    sort.value.order = (sort.value.order * -1) as 1 | -1
  }
  
  // Zachovat počet zobrazených záznamů, jen je seřadit znovu
  const currentCount = rows.value.length
  offset.value = 0
  // Dočasně zvýšit limit, aby se načetlo tolik záznamů, kolik jich bylo zobrazeno
  const originalLimit = limit.value
  limit.value = Math.max(currentCount, originalLimit)
  await fetchRecent(false)
  limit.value = originalLimit
  
  // Obnovit scroll pozici po překreslení
  await nextTick()
  if (tableScrollRef.value) {
    tableScrollRef.value.scrollLeft = scrollLeft
  }
  updateScrollFade()
}

// 💡 zvýraznění aktivního sloupce (až po prvním kliknutí)
const isActive = (k: string) => sort.value.key === k

// 🔽 seřazená data (nyní jen vrací rows, protože řazení je na BE)
const sortedRows = computed(() => rows.value)

onMounted(() => {
  fetchRecent(false)
  nextTick(updateScrollFade)
  window.addEventListener('resize', updateScrollFade)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateScrollFade)
})

watch(() => [props.userId, props.role, props.leagueId], () => {
  offset.value = 0
  fetchRecent(false)
}, { deep: true })

defineExpose({ total, rows, loadMore, loadingMore })
</script>




<style scoped>
/* match sticky-column bg to page background */
.hb-table-wrap {
  --v-theme-sheetBackground: var(--v-theme-background);
}

/* ── Component-specific layout ── */
.hb-games {
  padding: 0 4px;
}

/* Date column widths */
.th-date,
.td-date {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  white-space: nowrap;
}

/* Opponent column widths */
.th-opponent,
.td-opponent {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
}

/* Responsive column widths */
@media (min-width: 600px) {
  .th-date,
  .td-date {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
  }
  .th-opponent,
  .td-opponent {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
  }
}
</style>
